import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { CalendarDays, Download, Phone, Users, FileText, Loader2, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_ORDER = ['nova', 'em_contacto', 'reagendada', 'proposta', 'convertida', 'sem_interesse', 'invalida'];
const STATUS_COLORS = {
  nova: 'bg-slate-100 text-slate-700',
  em_contacto: 'bg-blue-100 text-blue-700',
  reagendada: 'bg-amber-100 text-amber-700',
  proposta: 'bg-violet-100 text-violet-700',
  convertida: 'bg-emerald-100 text-emerald-700',
  sem_interesse: 'bg-rose-100 text-rose-700',
  invalida: 'bg-gray-200 text-gray-600',
};

const todayISO = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Lisbon' });

const DailyReportPage = () => {
  const [dateFrom, setDateFrom] = useState(todayISO());
  const [dateTo, setDateTo] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [expanded, setExpanded] = useState({});

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/reports/daily-activity`, { params: { date_from: dateFrom, date_to: dateTo } });
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao carregar relatório');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => { fetchReport(); /* eslint-disable-next-line */ }, []);

  const setToday = () => { const t = todayISO(); setDateFrom(t); setDateTo(t); };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API_URL}/reports/daily-activity/export`, {
        params: { date_from: dateFrom, date_to: dateTo }, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `relatorio_diario_${dateFrom}_a_${dateTo}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Erro ao exportar Excel');
    } finally {
      setExporting(false);
    }
  };

  const labels = data?.status_labels || {};
  const totals = data?.totals;

  return (
    <Layout>
      <div className="space-y-6" data-testid="daily-report-page">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#2D3E6F] flex items-center justify-center">
            <CalendarDays className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Relatório Diário de Atividade</h1>
            <p className="text-sm text-slate-500">Acompanhe o que os comerciais fizeram às leads em qualquer período</p>
          </div>
        </div>

        {/* Filtros */}
        <Card>
          <CardContent className="p-4 flex flex-wrap items-end gap-3">
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">De</Label>
              <Input type="date" value={dateFrom} max={dateTo} onChange={(e) => setDateFrom(e.target.value)} className="w-[160px]" data-testid="report-date-from" />
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Até</Label>
              <Input type="date" value={dateTo} min={dateFrom} onChange={(e) => setDateTo(e.target.value)} className="w-[160px]" data-testid="report-date-to" />
            </div>
            <Button variant="outline" onClick={setToday} data-testid="report-today-btn">Hoje</Button>
            <Button onClick={fetchReport} disabled={loading} className="bg-[#2D3E6F] hover:bg-[#243257]" data-testid="report-apply-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
              Aplicar
            </Button>
            <div className="ml-auto">
              <Button onClick={handleExport} disabled={exporting || !data} variant="outline" className="border-emerald-600 text-emerald-700 hover:bg-emerald-50" data-testid="report-export-btn">
                {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Download className="w-4 h-4 mr-1" />}
                Exportar Excel
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Totais */}
        {totals && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="report-totals">
            <Card><CardContent className="p-4">
              <div className="flex items-center gap-2 text-slate-500 text-xs mb-1"><Phone className="w-4 h-4" /> Contactos feitos</div>
              <p className="text-2xl font-bold text-slate-800">{totals.contacts_made}</p>
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <div className="flex items-center gap-2 text-slate-500 text-xs mb-1"><Users className="w-4 h-4" /> Leads contactadas</div>
              <p className="text-2xl font-bold text-slate-800">{totals.leads_contacted}</p>
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <div className="flex items-center gap-2 text-slate-500 text-xs mb-1"><FileText className="w-4 h-4" /> Propostas</div>
              <p className="text-2xl font-bold text-violet-700">{totals.proposals}</p>
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <div className="flex items-center gap-2 text-slate-500 text-xs mb-1"><Users className="w-4 h-4" /> Comerciais ativos</div>
              <p className="text-2xl font-bold text-slate-800">{totals.commercials_active}</p>
            </CardContent></Card>
          </div>
        )}

        {/* Distribuição global por estado */}
        {totals && (
          <Card><CardContent className="p-4">
            <p className="text-sm font-semibold text-slate-700 mb-2">Distribuição atual por estado do Kanban</p>
            <div className="flex flex-wrap gap-2">
              {STATUS_ORDER.map((s) => (
                <span key={s} className={`text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS_COLORS[s]}`}>
                  {labels[s] || s}: {totals.by_status?.[s] || 0}
                </span>
              ))}
            </div>
          </CardContent></Card>
        )}

        {/* Detalhe por comercial */}
        <Card><CardContent className="p-0 overflow-x-auto">
          <table className="w-full min-w-[900px] text-sm" data-testid="report-commercial-table">
            <thead>
              <tr className="bg-slate-50 text-slate-600 text-left">
                <th className="p-3 font-semibold">Comercial</th>
                <th className="p-3 font-semibold text-center">Contactos</th>
                <th className="p-3 font-semibold text-center">Leads contact.</th>
                <th className="p-3 font-semibold text-center">Propostas</th>
                {STATUS_ORDER.map((s) => (
                  <th key={s} className="p-3 font-semibold text-center text-[11px]">{labels[s] || s}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(data?.commercials || []).map((cm) => (
                <React.Fragment key={cm.id}>
                  <tr className="border-t hover:bg-slate-50/60 cursor-pointer" onClick={() => setExpanded((p) => ({ ...p, [cm.id]: !p[cm.id] }))} data-testid={`report-comm-${cm.id}`}>
                    <td className="p-3 font-medium text-slate-800">{cm.name}
                      {cm.contacts_made > 0 && <span className="ml-2 text-[10px] text-[#2D3E6F]">{expanded[cm.id] ? '▲ ocultar' : '▼ ver contactos'}</span>}
                    </td>
                    <td className="p-3 text-center font-semibold">{cm.contacts_made}</td>
                    <td className="p-3 text-center">{cm.leads_contacted}</td>
                    <td className="p-3 text-center text-violet-700 font-semibold">{cm.proposals}</td>
                    {STATUS_ORDER.map((s) => (
                      <td key={s} className="p-3 text-center text-slate-600">{cm.by_status?.[s] || 0}</td>
                    ))}
                  </tr>
                  {expanded[cm.id] && cm.contacted_list?.length > 0 && (
                    <tr className="bg-slate-50/40">
                      <td colSpan={4 + STATUS_ORDER.length} className="p-3">
                        <p className="text-xs font-semibold text-slate-500 mb-2">Quem foi contactado ({cm.contacted_list.length}):</p>
                        <div className="flex flex-wrap gap-2">
                          {cm.contacted_list.map((it, i) => (
                            <span key={i} className="text-xs bg-white border border-slate-200 rounded px-2 py-1 max-w-[360px]" title={it.notes || ''}>
                              <span className="font-medium text-slate-700">{it.name}</span>
                              <span className="text-slate-400"> · {it.outcome}</span>
                              {it.notes ? <span className="block text-slate-500 truncate">📝 {it.notes}</span> : null}
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {(!data?.commercials || data.commercials.length === 0) && !loading && (
                <tr><td colSpan={4 + STATUS_ORDER.length} className="p-6 text-center text-slate-400">Sem dados para o período selecionado.</td></tr>
              )}
            </tbody>
          </table>
        </CardContent></Card>
      </div>
    </Layout>
  );
};

export default DailyReportPage;
